import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Skull, Flame, Star, Compass } from "lucide-react";
import { api } from "../api";
import { SectionEyebrow, SectionTitle, StarDivider } from "../components/Ornaments";
import { MediaEmbed } from "../components/MediaEmbed";
import { SEO } from "../components/SEO";
import { usePageContent, resolveMediaUrl } from "../components/PageContent";
import { useHeroSlides, usePageBlocks, PageBlocksRenderer } from "../components/PageBlocks";

const DEFAULT_HERO_SLIDES = [
  {
    id: "default-1",
    image:
      "https://images.unsplash.com/photo-1728333321391-fc828ef09f93?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
    eyebrow: "Volume IX",
    title: "Wheels West",
    subtitle: "A six-day expedition into the cracked pavement of the American interior.",
    cta_label: "Read the field report",
    cta_href: "/stories",
  },
  {
    id: "default-2",
    image:
      "https://images.unsplash.com/photo-1547235002-ad3f7478db46?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
    eyebrow: "Bad Idea №. 037",
    title: "Across the Mojave",
    subtitle: "We packed too many boards and not enough water. Documented in full.",
    cta_label: "See the evidence",
    cta_href: "/evidence",
  },
  {
    id: "default-3",
    image:
      "https://images.unsplash.com/photo-1602878714398-b28d40926846?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
    eyebrow: "New In The Shop",
    title: "The Derrick Deck",
    subtitle: "Named after the kindest security guard in Cherry Hill, New Jersey.",
    cta_label: "Spend some money",
    cta_href: "/shop",
  },
];

const HeroCarousel = () => {
  const slides = useHeroSlides("home", DEFAULT_HERO_SLIDES);
  const list = slides && slides.length ? slides : DEFAULT_HERO_SLIDES;
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    setIdx(0);
  }, [slides]);
  useEffect(() => {
    if (list.length <= 1) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % list.length), 6500);
    return () => clearInterval(t);
  }, [list.length]);
  const slide = list[idx] || list[0];
  if (!slide) return null;

  return (
    <section className="relative bg-asphalt text-bone overflow-hidden" data-testid="hero-carousel">
      <div className="relative h-[78vh] min-h-[520px] max-h-[820px]">
        {list.map((s, i) => (
          <div
            key={s.id || `slide-${i}`}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              i === idx ? "opacity-100" : "opacity-0"
            }`}
          >
            {s.image && (
              <img
                src={resolveMediaUrl(s.image)}
                alt={s.title || ""}
                className="w-full h-full object-cover ink-stamp"
                style={{ filter: "grayscale(70%) contrast(1.1) brightness(0.75)" }}
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-asphalt via-asphalt/40 to-asphalt/20" />
          </div>
        ))}

        <div className="relative h-full max-w-[1400px] mx-auto px-5 lg:px-10 flex flex-col justify-end pb-16 z-10">
          <div className="max-w-3xl">
            {slide.eyebrow && (
              <div className="font-mono text-xs uppercase tracking-[0.4em] text-terra mb-4" data-testid="hero-eyebrow">
                ✦ {slide.eyebrow}
              </div>
            )}
            {slide.title && (
              <h1
                className="font-display text-6xl sm:text-7xl md:text-8xl uppercase leading-[0.95] tracking-tighter mb-5"
                data-testid="hero-title"
              >
                {slide.title}
              </h1>
            )}
            {slide.subtitle && (
              <p className="font-western text-xl sm:text-2xl text-bone/90 mb-8 max-w-2xl">{slide.subtitle}</p>
            )}
            {slide.cta_label && slide.cta_href && (
              <Link
                to={slide.cta_href}
                data-testid="hero-cta"
                className="inline-flex items-center gap-3 bg-terra text-bone border-2 border-bone px-7 py-4 font-display tracking-wider uppercase text-xl shadow-hard-bone hover:translate-y-[2px] hover:translate-x-[2px] hover:shadow-[2px_2px_0_#F5F2E9] transition-all"
              >
                {slide.cta_label} <ArrowRight className="w-5 h-5" />
              </Link>
            )}
          </div>
        </div>

        {/* numeric pagination */}
        {list.length > 1 && (
          <div className="absolute bottom-6 right-6 z-10 flex gap-3 font-mono text-xs uppercase tracking-[0.3em]">
            {list.map((s, i) => (
              <button
                key={s.id || `dot-${i}`}
                onClick={() => setIdx(i)}
                data-testid={`hero-dot-${i}`}
                className={`px-3 py-2 border-2 border-bone ${
                  i === idx ? "bg-bone text-asphalt" : "bg-transparent text-bone hover:bg-bone/20"
                }`}
              >
                {String(i + 1).padStart(2, "0")}
              </button>
            ))}
            <div className="px-3 py-2 border-2 border-bone bg-transparent">/ {String(list.length).padStart(2, "0")}</div>
          </div>
        )}
      </div>
      <div className="tape-divider" />
    </section>
  );
};

const Mission = () => {
  const c = usePageContent("home", {
    eyebrow: "The Mission, Such As It Is",
    title: "Chibah Skateboards.\nCelebrating the best of our poor decisions since 2026.",
    subtitle: "Established under suspicious circumstances, we're here to document the trip.",
    body:
      "Catch up. Keep up. Cop some shit to help keep the lights on.\n\n" +
      "If our decks don't make you ollie higher or our shirts don't get you laid, we can't help you.",
  });
  return (
    <section className="bg-bone py-20 lg:py-28" data-testid="mission-section">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-4">
          {c.eyebrow && <SectionEyebrow testid="mission-eyebrow">{c.eyebrow}</SectionEyebrow>}
          <div className="font-display text-2xl uppercase tracking-tight leading-tight whitespace-pre-line">
            {c.title}
          </div>
        </div>
        <div className="lg:col-span-8">
          {c.subtitle && (
            <p className="font-western text-3xl md:text-4xl leading-snug mb-6">{c.subtitle}</p>
          )}
          {c.body && (
            <p className="font-body text-lg leading-relaxed text-ink max-w-3xl mb-6 whitespace-pre-line">
              {c.body}
            </p>
          )}
          {c.cta_label && c.cta_href && (
            <Link
              to={c.cta_href}
              className="inline-flex items-center gap-2 bg-asphalt text-bone border-2 border-asphalt px-5 py-3 font-display tracking-wider uppercase text-lg shadow-hard hover:bg-terra mb-6"
            >
              {c.cta_label}
            </Link>
          )}
          <div className="mt-10 space-y-5">
            {[
              { kind: "skull", label: "Authentic", sub: "According to multiple witnesses" },
              { kind: "flame", label: "Slightly Irreverent", sub: "Professionally unprofessional" },
              { kind: "compass", label: "Always Lost", sub: "We'll figure it out" },
            ].map((t) => (
              <div key={t.label} className="stamped px-6 py-6 flex items-center gap-6">
                <StampIcon kind={t.kind} />
                <div className="relative z-10 min-w-0">
                  <div className="font-display text-3xl sm:text-4xl uppercase tracking-tight leading-none">{t.label}</div>
                  <div className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.3em] text-ink mt-2">{t.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const LatestStories = ({ stories }) => (
  <section className="bg-card-light border-y-4 border-asphalt py-20 lg:py-24" data-testid="latest-stories-section">
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
      <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
        <div>
          <SectionEyebrow>Dispatches from the road</SectionEyebrow>
          <SectionTitle>Latest Stories</SectionTitle>
        </div>
        <Link
          to="/stories"
          data-testid="see-all-stories"
          className="inline-flex items-center gap-2 border-2 border-asphalt px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] shadow-hard hover:bg-asphalt hover:text-bone transition-colors"
        >
          See all stories <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {stories.slice(0, 3).map((s, i) => (
          <Link
            to={`/stories/${s.slug}`}
            key={s.id}
            data-testid={`story-card-${i}`}
            className="block border-2 border-asphalt bg-bone shadow-hard hover:-translate-y-1 hover:-translate-x-1 hover:shadow-hard-lg transition-all"
          >
            <div className="aspect-[4/3] overflow-hidden border-b-2 border-asphalt bg-asphalt">
              {s.cover_image && <img src={resolveMediaUrl(s.cover_image)} alt={s.title} className="w-full h-full object-cover ink-stamp" />}
            </div>
            <div className="p-5">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-terra mb-2">
                ✦ Story №. {String(i + 1).padStart(3, "0")}
              </div>
              <h3 className="font-display text-2xl uppercase tracking-tight leading-tight mb-2">{s.title}</h3>
              <p className="font-body text-sm text-ink line-clamp-3">{s.excerpt}</p>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] mt-4 text-ink">By {s.author || "The Crew"}</div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

const LatestEvidence = ({ evidence }) => (
  <section className="bg-asphalt text-bone py-20 lg:py-24" data-testid="latest-evidence-section">
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.35em] text-terra mb-3">✦ Visual record</div>
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl uppercase tracking-tighter">Latest Evidence</h2>
        </div>
        <Link
          to="/evidence"
          data-testid="see-all-evidence"
          className="inline-flex items-center gap-2 border-2 border-bone px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] shadow-hard-bone hover:bg-bone hover:text-asphalt transition-colors"
        >
          View archive <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 auto-rows-[180px] md:auto-rows-[220px]">
        {evidence.slice(0, 6).map((e, i) => {
          const spanClass =
            e.span === "large"
              ? "col-span-2 row-span-2"
              : e.span === "wide"
              ? "col-span-2"
              : e.span === "tall"
              ? "row-span-2"
              : "";
          return (
            <Link
              to="/evidence"
              key={e.id}
              data-testid={`evidence-tile-${i}`}
              className={`relative block border-2 border-bone overflow-hidden bg-card-dark group ${spanClass}`}
            >
              <MediaEmbed item={e} />
              {e.caption && (
                <div className="absolute bottom-0 left-0 right-0 p-3 bg-asphalt/85 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-bone">{e.caption}</div>
                </div>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  </section>
);

const FeaturedBadIdea = ({ badIdea }) => {
  if (!badIdea) return null;
  return (
    <section className="bg-bone py-20 lg:py-24" data-testid="featured-bad-idea-section">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
        <SectionEyebrow>From the Bureau of Bad Ideas</SectionEyebrow>
        <StarDivider className="text-terra mb-8" />
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 border-2 border-asphalt bg-card-light shadow-hard relative">
            <div className="absolute -top-4 -left-4 bg-terra text-bone px-3 py-1 font-mono text-[11px] uppercase tracking-[0.25em] border-2 border-asphalt">
              Risk Level: {badIdea.risk_level}
            </div>
            <div className="aspect-[16/9] overflow-hidden border-b-2 border-asphalt">
              {badIdea.cover_image && (
                <img src={resolveMediaUrl(badIdea.cover_image)} alt={badIdea.title} className="w-full h-full object-cover ink-stamp" />
              )}
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] mb-3 text-ink">Bureau Memo</div>
            <h2 className="font-display text-4xl sm:text-5xl uppercase leading-[0.95] tracking-tighter mb-4">
              {badIdea.title}
            </h2>
            <p className="font-western text-2xl text-ink mb-5">{badIdea.tagline}</p>
            <p className="font-body text-base text-ink mb-8 whitespace-pre-line line-clamp-6">
              {badIdea.body}
            </p>
            <Link
              to={`/bad-ideas/${badIdea.slug}`}
              data-testid="featured-bad-idea-cta"
              className="inline-flex items-center gap-2 bg-asphalt text-bone border-2 border-asphalt px-6 py-3 font-display tracking-wider uppercase text-lg shadow-hard hover:bg-terra transition-colors"
            >
              Read the memo <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

const CrewSpotlight = ({ crew }) => {
  const featured = crew.filter((c) => c.featured).slice(0, 2);
  const showcase = featured.length ? featured : crew.slice(0, 2);
  return (
    <section className="bg-card-light border-y-4 border-asphalt py-20 lg:py-24" data-testid="crew-spotlight-section">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <SectionEyebrow>By order of the Marshal</SectionEyebrow>
            <h2 className="font-western text-4xl sm:text-5xl">Crew Spotlight</h2>
          </div>
          <Link
            to="/crew"
            data-testid="see-all-crew"
            className="inline-flex items-center gap-2 border-2 border-asphalt px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] shadow-hard hover:bg-asphalt hover:text-bone transition-colors"
          >
            Meet the gang <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid md:grid-cols-2 gap-8">
          {showcase.map((c, i) => (
            <WantedPoster crew={c} key={c.id} testid={`crew-spotlight-${i}`} linkable />
          ))}
        </div>
      </div>
    </section>
  );
};

export const WantedPoster = ({ crew, testid, linkable = false }) => {
  const inner = (
    <>
      <div className="border-b-2 border-asphalt p-4 text-center">
        <div className="font-mono text-[10px] uppercase tracking-[0.4em]">By Authority of the Dog House</div>
        <div className="font-western text-3xl sm:text-4xl mt-1">WANTED</div>
        <div className="font-mono text-[10px] uppercase tracking-[0.4em]">— Alive, ideally —</div>
      </div>
      <div className="p-5 border-b-2 border-asphalt">
        <div className="aspect-[4/3] overflow-hidden border-2 border-asphalt bg-asphalt">
          {crew.photo && (
            <img src={resolveMediaUrl(crew.photo)} alt={crew.name} className="w-full h-full object-cover ink-stamp" style={{ filter: "grayscale(100%) contrast(1.2)" }} />
          )}
        </div>
      </div>
      <div className="p-5 text-center border-b-2 border-asphalt">
        <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-ink">Alias</div>
        <div className="font-western text-2xl sm:text-3xl">&ldquo;{crew.alias}&rdquo;</div>
        <div className="font-display text-2xl uppercase tracking-tight mt-1">{crew.name}</div>
      </div>
      <div className="grid grid-cols-2 divide-x-2 divide-asphalt border-b-2 border-asphalt">
        <div className="p-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink">Hails from</div>
          <div className="font-body text-base">{crew.hometown || "Unknown"}</div>
        </div>
        <div className="p-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink">Bounty</div>
          <div className="font-body text-base">{crew.bounty || "Negotiable"}</div>
        </div>
      </div>
      <div className="p-5">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink mb-2">Known Crimes</div>
        <ul className="font-body text-sm space-y-1 list-disc list-inside">
          {(crew.crimes || []).map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
        {linkable && (
          <div className="mt-4 pt-4 border-t-2 border-dashed border-asphalt/40 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-terra">
            ✦ View Dossier →
          </div>
        )}
      </div>
    </>
  );

  if (linkable && crew.slug) {
    return (
      <Link
        to={`/crew/${crew.slug}`}
        data-testid={testid}
        className="block border-4 border-asphalt bg-bone shadow-hard hover:-translate-y-1 hover:-translate-x-1 hover:shadow-hard-lg transition-all focus:outline-none focus:ring-2 focus:ring-terra"
      >
        {inner}
      </Link>
    );
  }
  return (
    <div className="border-4 border-asphalt bg-bone shadow-hard" data-testid={testid}>
      {inner}
    </div>
  );
};

const FeaturedProducts = ({ products, onAdd }) => {
  const items = products.filter((p) => p.featured).slice(0, 3);
  const show = items.length ? items : products.slice(0, 3);
  return (
    <section className="bg-bone py-20 lg:py-24" data-testid="featured-products-section">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
        <div className="text-center mb-12">
          <SectionEyebrow>Goods of Questionable Necessity</SectionEyebrow>
          <h2 className="font-western text-4xl sm:text-5xl">Featured Products</h2>
          <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-ink mt-3">
            ✦ Guaranteed to cure boredom or your money back* ✦
          </p>
          <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-ink/60 mt-1">(*money will not be returned)</p>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {show.map((p, i) => (
            <ProductCard product={p} key={p.id} index={i} onAdd={onAdd} />
          ))}
        </div>
        <div className="text-center mt-12">
          <Link
            to="/shop"
            data-testid="see-all-products"
            className="inline-flex items-center gap-3 bg-terra text-bone border-2 border-asphalt px-7 py-4 font-display tracking-wider uppercase text-xl shadow-hard hover:translate-y-[2px] hover:translate-x-[2px] hover:shadow-[2px_2px_0_#1C1B1A] transition-all"
          >
            Spend some money <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export const ProductCard = ({ product, index, onAdd }) => (
  <div
    className="border-2 border-asphalt bg-card-light shadow-hard hover:-translate-y-1 hover:-translate-x-1 hover:shadow-hard-lg transition-all flex flex-col"
    data-testid={`product-card-${index}`}
  >
    <Link to={`/shop/${product.slug}`} className="block">
      <div className="border-b-2 border-asphalt bg-bone p-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-ink text-center">Item №. {String(index + 1).padStart(3, "0")}</div>
      </div>
      <div className="aspect-square overflow-hidden border-b-2 border-asphalt bg-bone">
        {product.image && <img src={resolveMediaUrl(product.image)} alt={product.name} className="w-full h-full object-cover" loading="lazy" />}
      </div>
    </Link>
    <div className="p-5 text-center border-b-2 border-asphalt">
      <div className="font-display text-2xl uppercase tracking-tight">{product.name}</div>
      <div className="font-western text-base text-terra mt-1">{product.tagline}</div>
    </div>
    <div className="p-5 flex items-center justify-between">
      <div className="font-display text-3xl">${product.price.toFixed(2)}</div>
      <button
        onClick={() => onAdd(product)}
        data-testid={`add-to-cart-${index}`}
        className="bg-asphalt text-bone border-2 border-asphalt px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] hover:bg-terra hover:border-terra transition-colors"
      >
        Add to Bag
      </button>
    </div>
  </div>
);

import { useCart } from "../context/CartContext";
import { toast } from "sonner";

const ICON_CHOICE = { skull: "e", flame: "c", compass: "a" };

const StampIcon = ({ kind }) => (
  <img
    src={`/icons/${kind}-${ICON_CHOICE[kind]}.png`}
    alt={kind}
    className="w-20 h-20 sm:w-24 sm:h-24 shrink-0 object-contain relative z-10"
    style={{ mixBlendMode: "multiply" }}
  />
);

export default function Home() {
  const [stories, setStories] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [crew, setCrew] = useState([]);
  const [products, setProducts] = useState([]);
  const [badIdeas, setBadIdeas] = useState([]);
  const homeBlocks = usePageBlocks("home");
  const { add } = useCart();

  useEffect(() => {
    Promise.all([
      api.get("/stories").then((r) => setStories(r.data)),
      api.get("/evidence").then((r) => setEvidence(r.data)),
      api.get("/crew").then((r) => setCrew(r.data)),
      api.get("/shop/products?limit=24").then((r) => setProducts(r.data?.items || [])),
      api.get("/bad-ideas").then((r) => setBadIdeas(r.data)),
    ]).catch(() => {});
  }, []);

  const handleAdd = (p) => {
    add(p, 1);
    toast.success(`${p.name} added to the bag.`);
  };

  const featuredBadIdea = badIdeas.find((b) => b.featured) || badIdeas[0];

  return (
    <div data-testid="home-page">
      <SEO path="/" />
      <HeroCarousel />
      <Mission />
      <PageBlocksRenderer blocks={homeBlocks} testid="home-blocks" />
      <LatestStories stories={stories} />
      <LatestEvidence evidence={evidence} />
      <FeaturedBadIdea badIdea={featuredBadIdea} />
      <CrewSpotlight crew={crew} />
      <FeaturedProducts products={products} onAdd={handleAdd} />
    </div>
  );
}
